import React, { useEffect, useMemo, useRef } from 'react';
import { useHilReader, useHilStore } from '@bizfirst/hil-react';
import { HilChatWindowChrome } from './HilChatWindowChrome';
import { HilChatTranscript } from '../transcript/HilChatTranscript';
import { HilChatComposer } from '../composer/HilChatComposer';
import type { HilChatComposerHandle } from '../composer/HilChatComposer';
import { useHilChatMessages } from '../hooks/useHilChatMessages';
import { useHilChatTranscriptStore } from '../store/hilChatTranscriptStore';
import type { HilChatMessage, HilChatMessageStatus } from '../types/HilChatMessage';
import type { HilChatWindowSettings } from '../settings/HilChatWindowSettings';

interface HilChatWindowProps {
  settings: HilChatWindowSettings;
  onClose?: () => void;
}

/**
 * Pure composition — contains no rendering logic of its own beyond assembling
 * chrome + transcript + composer. All conversation-transcript state this package
 * introduces (the client-local echo of the user's own sent turns — the reader has
 * no visibility into submitted reply data — the wizard's currentStep/form-data, and
 * the past-turn `systemHistory` buffer for re-suspending sessions) lives in
 * `useHilChatTranscriptStore`: a module-singleton Zustand store, the SAME
 * architecture `@bizfirst/hil-react`'s `useHilStore` already uses for the active
 * HIL envelope. That matters because this component is unmounted and remounted
 * every time the Observe panel's active tab changes away from "HIL Actions" and
 * back (flow-observer-panel's TabContent only keeps the currently-active tab's
 * content component mounted) — local component state (useState/useRef) does not
 * survive that, but a module singleton does, which is exactly the property this
 * package needs. See hilChatTranscriptStore.ts for the full reasoning, and
 * DevelopmentHistoryLog.md for the bug this replaced.
 *
 * `systemHistory` exists because `useHilChatMessages` derives purely from the
 * CURRENTLY active envelope's Presentations[] — for a single-shot HIL node
 * (Form/Approval/Core's Chat) that's the whole story, since the active envelope
 * never changes mid-session. For a re-suspending node, each new SignalR push
 * replaces the active envelope entirely, so without this buffer a later turn's
 * system message would silently replace an earlier one in the transcript instead
 * of appending below it. See `captureSwap` in the store for how a past turn is
 * detected and captured.
 */
export function HilChatWindow({ settings, onClose }: HilChatWindowProps): React.JSX.Element | null {
  const reader = useHilReader();
  const activeEnvelope = useHilStore(s => s.active);
  const systemMessages = useHilChatMessages(settings);

  const currentStep = useHilChatTranscriptStore(s => s.currentStep);
  const currentFormData = useHilChatTranscriptStore(s => s.currentFormData);
  const echoed = useHilChatTranscriptStore(s => s.echoed);
  const systemHistory = useHilChatTranscriptStore(s => s.systemHistory);
  const ensureSession = useHilChatTranscriptStore(s => s.ensureSession);
  const setCurrentStep = useHilChatTranscriptStore(s => s.setCurrentStep);
  const setCurrentFormData = useHilChatTranscriptStore(s => s.setCurrentFormData);
  const addEcho = useHilChatTranscriptStore(s => s.addEcho);
  const updateEchoStatus = useHilChatTranscriptStore(s => s.updateEchoStatus);
  const allocateEchoID = useHilChatTranscriptStore(s => s.allocateEchoID);
  const captureSwap = useHilChatTranscriptStore(s => s.captureSwap);

  const composerRef = useRef<HilChatComposerHandle>(null);
  const activeSessionID = reader?.getEngageSessionID() ?? null;

  // Idempotent against the store's own memory of the last session it served — see
  // `ensureSession`'s doc comment. Firing this on every mount (including a remount after a
  // tab-switch-away-and-back) is intentional and safe: it only actually resets the transcript
  // when `activeSessionID` genuinely differs from what the store last recorded.
  useEffect(() => {
    ensureSession(activeSessionID);
  }, [activeSessionID, ensureSession]);

  const visibleSystemMessages = useMemo(
    () => systemMessages.filter(m => m.order <= currentStep),
    [systemMessages, currentStep]
  );

  // Capture-on-swap: runs after every render (deliberately no dependency array — correctness
  // comes from the store's own reference comparison, not from firing only once per change), so
  // it stays correct regardless of what else in this component causes a re-render, AND across
  // this component unmounting/remounting entirely (see hilChatTranscriptStore.ts's captureSwap).
  useEffect(() => {
    captureSwap(activeEnvelope, visibleSystemMessages);
  });

  const merged = useMemo(() => {
    if (systemHistory.length === 0) {
      // No captured past turns — identical to a single-shot HIL node's behavior: sort the
      // current turn's system + echoed messages by their own numeric order.
      return [...visibleSystemMessages, ...echoed].sort((a, b) => a.order - b.order);
    }
    // Past turns exist: interleave each captured turn with the echoed replies that were sent
    // before it was superseded (echoed has no per-turn Order to key on — see
    // HilChatSystemHistoryEntry doc above), then append the current live turn the same way
    // single-shot nodes already do.
    const result: HilChatMessage[] = [];
    let boundary = 0;
    for (const entry of systemHistory) {
      result.push(entry.message, ...echoed.slice(boundary, entry.echoedBoundary));
      boundary = entry.echoedBoundary;
    }
    result.push(...[...visibleSystemMessages, ...echoed.slice(boundary)].sort((a, b) => a.order - b.order));
    return result;
  }, [systemHistory, visibleSystemMessages, echoed]);

  if (!reader) return null;

  const handleMessageSent = (message: HilChatMessage) => {
    addEcho({ ...message, order: currentStep });
  };

  const handleMessageStatusChange = (id: string, status: HilChatMessageStatus) => {
    updateEchoStatus(id, status);
  };

  const handleFormValuesChange = (messageID: string, values: Record<string, unknown>) => {
    // Only the current step's own form bubble may update currentFormData — a prior
    // step's already-submitted form bubble is read-only (see HilChatTranscript's
    // per-message disabled prop below) and must not be able to corrupt the active
    // step's submission by editing after the fact.
    if (messageID !== `${activeSessionID ?? 0}-p${currentStep}`) return;
    setCurrentFormData(values);
  };

  const handleRetry = (messageID: string) => {
    const message = echoed.find(m => m.id === messageID);
    if (message) composerRef.current?.retry(message);
  };

  return (
    <HilChatWindowChrome settings={settings} title={reader.title()} deadline={reader.deadline()} onClose={onClose}>
      <HilChatTranscript
        messages={merged}
        settings={settings}
        currentStep={currentStep}
        onFormValuesChange={handleFormValuesChange}
        onRetry={handleRetry}
      />
      <HilChatComposer
        ref={composerRef}
        settings={settings}
        currentStep={currentStep}
        currentFormData={currentFormData}
        onAdvance={setCurrentStep}
        onMessageSent={handleMessageSent}
        onMessageStatusChange={handleMessageStatusChange}
        nextEchoID={allocateEchoID}
      />
    </HilChatWindowChrome>
  );
}
